<template>
  <div class="garage-page cols full">
    <div class="rows mb-10 garage-filters">
      <div class="cols align-items-start pr-10">
        <label for="vehicleFilter">{{ $t('task.vehicle') }}</label>
        <ChooseVehicles
            id="vehicleFilter"
            v-model="vehicleFilterModel"
            :preselect-all="false"
            :single-select="false"
            :add-all-option="false"
        />
      </div>
      <div class="cols align-items-start pr-10">
        <label for="statusFilter">{{ $t('maintenance.status') }}</label>
        <ChooseMaintenanceStatus
            id="statusFilter"
            v-model="statusFilterModel"
            :multiple="true"
        />
      </div>
      <div class="cols align-items-start">
        <label>&nbsp;</label>
        <button type="button" class="search-button" @click="loadMaintenances">
          {{ $t('reports.trips.search') }}
        </button>
      </div>
    </div>

    <Accordion
        v-for="item in vehiclesWithMaintenances"
        :key="item.vehicle.id"
        class="mb-10"
        :expanded="false"
        @expanded="setExpanded(item.vehicle.id, true)"
        @collapsed="setExpanded(item.vehicle.id, false)"
    >
      <template #title>
        <div class="rows width-100p align-items-center vehicle-title">
          <div class="rows align-items-center vehicle-name-row">
            <span class="vehicle-name">{{ item.vehicle.fullName }}</span>
            <span class="vehicle-plate">{{ item.vehicle.licensePlate }}</span>
          </div>
          <div class="rows align-items-center status-counters">
            <div class="counter upcoming">
              {{ $t('maintenance.count.upcoming') }}: {{ item.upcomingCount }}
            </div>
            <div class="counter due">
              {{ $t('maintenance.count.due') }}: {{ item.dueCount }}
            </div>
            <div class="counter overdue">
              {{ $t('maintenance.count.overdue') }}: {{ item.overdueCount }}
            </div>
            <div class="counter completed">
              {{ $t('maintenance.count.completed') }}: {{ item.completedCount }}
            </div>
          </div>
        </div>
      </template>
      <template #content>
        <div class="maintenance-content">
          <button
              v-if="expandedVehicles[item.vehicle.id]"
              type="button"
              class="mb-10"
              @click="openCreateMaintenancePopup(item.vehicle)"
          >
            {{ $t('maintenance.action.create') }}
          </button>
          <div v-if="item.maintenances.length === 0" class="empty-maintenances">
            {{ $t('maintenance.empty') }}
          </div>
          <DataGrid
              v-else
              class="maintenance-data-grid"
              :data="item.maintenances"
              :data-model="maintenanceDataGridModel(item.vehicle)"
              :interactive="false"
          />
        </div>
      </template>
    </Accordion>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import Accordion from "@/components/pages/partials/Accordion.vue";
import {MaintenanceStatus} from "@/assets/js/enums/MaintenanceStatus.ts";
import {createApp} from "vue";
import {actions, mutations} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import CreateMaintenanceForm from "@/components/pages/garage/CreateMaintenanceForm.vue";
import {popups} from "@/assets/js/popups";
import {dataManipulator, entityDataManager} from "@/assets/js/data-manipulation";
import i18n from "@/assets/js/vue/i18n";
import store from '@/assets/js/vue/vuex';
import DataGrid from "@/components/pages/partials/DataGrid.vue";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";
import ChooseMaintenanceStatus from "@/components/pages/partials/ChooseMaintenanceStatus.vue";
import ImportantMaintenancesPopup from "@/components/pages/garage/ImportantMaintenancesPopup.vue";

export default {
  name: 'Garage',
  components: {Accordion, DataGrid, ChooseVehicles, ChooseMaintenanceStatus},
  computed: {
    ...mapState(['vehicles', 'maintenances', 'positions', 'garageFilterDevice', 'garageFilterStatus']),
    vehicleFilterModel: {
      get() {
        return this.garageFilterDevice;
      },
      set(value) {
        this.$store.commit(mutations.SET_GARAGE_FILTER_DEVICE, value);
      }
    },
    statusFilterModel: {
      get() {
        return this.garageFilterStatus;
      },
      set(value) {
        this.$store.commit(mutations.SET_GARAGE_FILTER_STATUS, value);
      }
    },
    vehiclesWithMaintenances() {
      const allVehicles = [...(this.vehicles ?? [])];

      const vehicles = this.appliedGarageFilterDeviceIds.length > 0
          ? allVehicles.filter(vehicle => this.appliedGarageFilterDeviceIds.includes(vehicle?.deviceId))
          : allVehicles;

      const mappedVehicles = vehicles.map(vehicle => {
        const allVehicleMaintenances = this.sortMaintenancesByDefault(this.maintenancesByDeviceId[vehicle.deviceId] ?? []);

        return {
          vehicle,
          maintenances: allVehicleMaintenances,
          upcomingCount: this.getStatusCount(allVehicleMaintenances, MaintenanceStatus.Upcoming),
          dueCount: this.getStatusCount(allVehicleMaintenances, MaintenanceStatus.Due),
          overdueCount: this.getStatusCount(allVehicleMaintenances, MaintenanceStatus.Overdue),
          completedCount: this.getStatusCount(allVehicleMaintenances, MaintenanceStatus.Completed),
        };
      });

      return mappedVehicles.sort((a, b) => {
        if (b.overdueCount !== a.overdueCount) {
          return b.overdueCount - a.overdueCount;
        }
        if (b.dueCount !== a.dueCount) {
          return b.dueCount - a.dueCount;
        }
        if (b.upcomingCount !== a.upcomingCount) {
          return b.upcomingCount - a.upcomingCount;
        }
        if (b.completedCount !== a.completedCount) {
          return b.completedCount - a.completedCount;
        }

        const firstName = (a.vehicle?.fullName ?? '').toLowerCase();
        const secondName = (b.vehicle?.fullName ?? '').toLowerCase();
        return firstName.localeCompare(secondName);
      });
    },
    maintenancesByDeviceId() {
      return (this.maintenances ?? []).reduce((result, maintenance) => {
        const deviceId = maintenance?.deviceId;
        if (deviceId === null || deviceId === undefined) {
          return result;
        }

        if (result[deviceId] === undefined) {
          result[deviceId] = [];
        }

        result[deviceId].push(maintenance);
        return result;
      }, {});
    },
  },
  watch: {
    maintenances(newValue, oldValue) {
      if (!oldValue?.length && newValue?.length > 0) {
        this.openImportantMaintenancesPopupIfNeeded();
      }
    }
  },
  async mounted() {
    if (this.maintenances?.length > 0) {
      await this.openImportantMaintenancesPopupIfNeeded();
    }
  },
  methods: {
    getVehicleNameByDeviceId(deviceId) {
      const vehicle = (this.vehicles ?? []).find(x => x?.deviceId === deviceId);
      return vehicle?.fullName ?? '-';
    },
    getVehicleLicensePlateByDeviceId(deviceId) {
      const vehicle = (this.vehicles ?? []).find(x => x?.deviceId === deviceId);
      return vehicle?.licensePlate ?? '-';
    },
    getImportantMaintenancesForPopup() {
      const dueAndOverdue = (this.maintenances ?? []).filter(x => {
        const status = this.normalizeStatus(x?.status);
        return status === MaintenanceStatus.Overdue || status === MaintenanceStatus.Due;
      });

      const statusPriority = {
        [MaintenanceStatus.Overdue]: 0,
        [MaintenanceStatus.Due]: 1,
      };

      return [...dueAndOverdue].sort((a, b) => {
        const firstPriority = statusPriority[this.normalizeStatus(a?.status)] ?? Number.MAX_SAFE_INTEGER;
        const secondPriority = statusPriority[this.normalizeStatus(b?.status)] ?? Number.MAX_SAFE_INTEGER;
        if (firstPriority !== secondPriority) {
          return firstPriority - secondPriority;
        }

        const firstVehicleName = this.getVehicleNameByDeviceId(a?.deviceId).toLowerCase();
        const secondVehicleName = this.getVehicleNameByDeviceId(b?.deviceId).toLowerCase();
        return firstVehicleName.localeCompare(secondVehicleName);
      });
    },
    async openImportantMaintenancesPopupIfNeeded() {
      const importantMaintenances = this.getImportantMaintenancesForPopup();
      if (!importantMaintenances?.length) {
        return;
      }

      const wrapper = document.createElement('div');
      let componentApp = null;
      const popupItems = importantMaintenances.map(maintenance => ({
        id: maintenance?.id,
        vehicleName: this.getVehicleNameByDeviceId(maintenance?.deviceId),
        licensePlate: this.getVehicleLicensePlateByDeviceId(maintenance?.deviceId),
        status: this.getStatusLabel(maintenance?.status),
        maintenanceName: maintenance?.name ?? '-',
        maintenanceDescription: maintenance?.description || '-',
      }));

      componentApp = createApp(ImportantMaintenancesPopup, {
        items: popupItems,
        onClose: async () => {
          await this.$swal.close();
        }
      });
      componentApp.use(i18n);
      componentApp.mount(wrapper);

      await this.$swal.fire({
        title: this.$t('maintenance.popup.title-important'),
        html: wrapper,
        width: '980px',
        showConfirmButton: false,
        showDenyButton: false,
        showCancelButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: false,
        customClass: {
          title: 'text-danger',
          htmlContainer: 'important-maintenances-popup'
        },
        didClose: () => componentApp?.unmount(),
      });
    },
    getMaintenancesFilterPayload() {
      const payload = {
        deviceId: [],
        status: [],
      };

      const selectedDeviceIds = (this.garageFilterDevice ?? [])
          .map(x => x?.deviceId)
          .filter(x => x !== null && x !== undefined);
      if (selectedDeviceIds.length > 0) {
        payload.deviceId = selectedDeviceIds;
      }

      const selectedStatuses = (this.garageFilterStatus ?? [])
          .map(x => x?.value)
          .filter(x => x !== null && x !== undefined);
      if (selectedStatuses.length > 0) {
        payload.status = selectedStatuses;
      }

      return payload;
    },
    async loadMaintenances() {
      try {
        this.appliedGarageFilterDeviceIds = (this.garageFilterDevice ?? [])
            .map(x => x?.deviceId)
            .filter(x => x !== null && x !== undefined);
        await this.$store.dispatch(actions.GET_MAINTENANCES_BY_USER, this.getMaintenancesFilterPayload());
      }
      catch (e) {
        notification.error(this.$t(e.message));
      }
    },
    setExpanded(vehicleId, isExpanded) {
      this.expandedVehicles = {
        ...this.expandedVehicles,
        [vehicleId]: isExpanded
      };
    },
    async createMaintenance(vehicle, formData) {
      const odometerPeriodInMeters = this.kilometersToMeters(formData?.odometerPeriod);
      const targetOdometerInMeters = this.kilometersToMeters(formData?.targetOdometer);

      await this.$store.dispatch(actions.CREATE_MAINTENANCE_BY_DEVICE_ID, {
        deviceId: vehicle.deviceId,
        ...formData,
        odometerPeriod: odometerPeriodInMeters,
        targetOdometer: targetOdometerInMeters,
      });
      await this.loadMaintenances();
      notification.success(this.$t('generic.save.success'));
    },
    async editMaintenance(vehicle, maintenance, formData) {
      const odometerPeriodInMeters = this.kilometersToMeters(formData?.odometerPeriod);
      const targetOdometerInMeters = this.kilometersToMeters(formData?.targetOdometer);

      await this.$store.dispatch(actions.EDIT_MAINTENANCE, {
        id: maintenance.id,
        deviceId: vehicle.deviceId,
        ...formData,
        odometerPeriod: odometerPeriodInMeters,
        targetOdometer: targetOdometerInMeters,
      });
      await this.loadMaintenances();
      notification.success(this.$t('generic.save.success'));
    },
    async openMaintenancePopup(title, vehicle, onSaveHandler = null, initialData = null, options = {}) {
      const wrapper = document.createElement('div');
      let componentApp = null;

      const onSave = async (formData) => {
        try {
          if (typeof onSaveHandler === 'function') {
            await onSaveHandler(formData);
          }
          await this.$swal.close();
        }
        catch (e) {
          notification.error(this.$t(e.message));
        }
      };

      const onCancel = async () => {
        await this.$swal.close();
      };

      componentApp = createApp(CreateMaintenanceForm, {
        onSave,
        onCancel,
        initialData,
        vehicle,
        completeMode: options.completeMode === true,
        readOnlyMode: options.readOnlyMode === true,
        editMode: options.editMode === true,
      });
      componentApp.use(i18n);
      componentApp.use(store);
      componentApp.mount(wrapper);

      await this.$swal.fire({
        title,
        html: wrapper,
        showConfirmButton: false,
        showCancelButton: false,
        didClose: () => componentApp?.unmount(),
      });
    },
    async openCreateMaintenancePopup(vehicle) {
      await this.openMaintenancePopup(
          this.$t('maintenance.title.create'),
          vehicle,
          (formData) => this.createMaintenance(vehicle, formData),
          {
            currentOdometer: this.getCurrentOdometerByDeviceId(vehicle.deviceId),
          },
      );
    },
    async openEditMaintenancePopup(vehicle, maintenance) {
      await this.openMaintenancePopup(
          this.$t('maintenance.title.edit'),
          vehicle,
          (formData) => this.editMaintenance(vehicle, maintenance, formData),
          {
            name: maintenance.name,
            description: maintenance.description,
            statusLabel: this.getStatusLabel(maintenance.status),
            amount: maintenance.amount,
            odometerPeriod: this.metersToKilometers(maintenance.odometerPeriod),
            targetOdometer: this.metersToKilometers(maintenance.targetOdometer),
            odometerPeriodConfigured: maintenance.odometerPeriodConfigured,
            targetDateConfigured: maintenance.targetDateConfigured,
            currentOdometer: this.getCurrentOdometerByDeviceId(vehicle.deviceId),
            targetDate: maintenance.targetDate,
            completionDate: maintenance.completionDate,
          },
          {editMode: true},
      );
    },
    async copyMaintenance(vehicle, maintenance) {
      await this.openMaintenancePopup(
          this.$t('maintenance.title.copy'),
          vehicle,
          (formData) => this.createMaintenance(vehicle, formData),
          {
            name: maintenance.name,
            description: maintenance.description,
            amount: maintenance.amount,
            odometerPeriod: this.metersToKilometers(maintenance.odometerPeriod),
            targetOdometer: this.metersToKilometers(maintenance.targetOdometer),
            odometerPeriodConfigured: maintenance.odometerPeriodConfigured,
            targetDateConfigured: maintenance.targetDateConfigured,
            currentOdometer: this.getCurrentOdometerByDeviceId(vehicle.deviceId),
            targetDate: maintenance.targetDate,
          },
      );
    },
    async completeMaintenance(maintenance, formData) {
      const odometerPeriodInMeters = this.kilometersToMeters(formData?.odometerPeriod);
      const targetOdometerInMeters = this.kilometersToMeters(formData?.targetOdometer);
      const maintenanceName = formData?.name ?? maintenance?.name ?? null;
      const maintenanceType = Number(maintenance?.type);
      let dismissTypedData = false;

      if (maintenanceType === 2) {
        const confirmationText = this.$t('maintenance.complete.dismiss-typed-data.confirm', {
          maintenanceName: maintenanceName ?? '-',
        });
        dismissTypedData = (await popups.yesNoConfirmation(this.$swal, confirmationText))?.isConfirmed;
      }

      await this.$store.dispatch(actions.COMPLETE_MAINTENANCE, {
        id: maintenance.id,
        name: maintenanceName,
        description: formData?.description ?? maintenance?.description ?? null,
        amount: formData?.amount ?? null,
        reschedule: formData?.reschedule ?? false,
        odometerPeriod: odometerPeriodInMeters,
        targetOdometer: targetOdometerInMeters,
        odometerPeriodConfigured: formData?.odometerPeriodConfigured ?? null,
        targetDate: formData?.targetDate ?? null,
        targetDateConfigured: formData?.targetDateConfigured ?? null,
        dismissTypedData,
      });
      await this.loadMaintenances();
      notification.success(this.$t('generic.save.success'));
    },
    async openCompleteMaintenancePopup(maintenance, vehicle) {
      await this.openMaintenancePopup(
          this.$t('maintenance.title.complete'),
          vehicle,
          (formData) => this.completeMaintenance(maintenance, formData),
          {
            name: maintenance.name,
            description: maintenance.description,
            amount: maintenance.amount,
            odometerPeriod: this.metersToKilometers(maintenance.odometerPeriod),
            targetOdometer: this.metersToKilometers(maintenance.targetOdometer),
            odometerPeriodConfigured: maintenance.odometerPeriodConfigured,
            targetDateConfigured: maintenance.targetDateConfigured,
            currentOdometer: this.getCurrentOdometerByDeviceId(maintenance.deviceId),
            targetDate: maintenance.targetDate,
            completionDate: maintenance.completionDate,
          },
          {completeMode: true},
      );
    },
    async openViewMaintenancePopup(maintenance, vehicle) {
      await this.openMaintenancePopup(
          this.$t('maintenance.title.view'),
          vehicle,
          null,
          {
            name: maintenance.name,
            description: maintenance.description,
            amount: maintenance.amount,
            odometerPeriod: this.metersToKilometers(maintenance.odometerPeriod),
            targetOdometer: this.metersToKilometers(maintenance.targetOdometer),
            odometerPeriodConfigured: maintenance.odometerPeriodConfigured,
            targetDateConfigured: maintenance.targetDateConfigured,
            currentOdometer: this.getCurrentOdometerByDeviceId(maintenance.deviceId),
            targetDate: maintenance.targetDate,
            completionDate: maintenance.completionDate,
          },
          {readOnlyMode: true},
      );
    },
    async deleteMaintenance(maintenance) {
      const result = await popups.yesNoConfirmation(this.$swal, this.$t('generic.delete.confirm'));
      if (!result.isConfirmed) {
        return;
      }

      try {
        await this.$store.dispatch(actions.DELETE_MAINTENANCE, {id: maintenance.id});
        await this.loadMaintenances();
        notification.success(this.$t('generic.delete.success'));
      }
      catch (e) {
        notification.error(this.$t(e.message));
      }
    },
    maintenanceDataGridModel(vehicle) {
      return {
        headers: {
          [this.$t('maintenance.name')]: 'name',
          [this.$t('maintenance.status')]: 'status',
          [this.$t('maintenance.description')]: 'description',
          [this.$t('maintenance.amount')]: 'amount',
        },
        values: {
          name: maintenance => maintenance?.name ?? '-',
          status: maintenance => {
            const statusLabel = this.getStatusLabel(maintenance?.status);
            if (this.isMaintenanceCompleted(maintenance)) {
              return `${statusLabel} ${this.$t('maintenance.completed.at-on', {
                odometer: this.formatCompletionOdometer(maintenance?.completionOdometer),
                date: this.formatCompletionDate(maintenance?.completionDate),
              })}`;
            }

            return `${statusLabel}${this.formatPlannedStatusDetails(maintenance, vehicle.deviceId)}`;
          },
          description: maintenance => maintenance?.description || '-',
          amount: maintenance => this.formatAmount(maintenance?.amount),
        },
        actions: [
          {
            icon: 'bi bi-eye',
            title: this.$t('maintenance.title.view'),
            handler: maintenance => this.openViewMaintenancePopup(maintenance, vehicle),
          },
          {
            icon: 'bi bi-check-square',
            buttonClass: 'accent-action-button',
            title: this.$t('maintenance.title.complete'),
            isVisible: maintenance => !this.isMaintenanceCompleted(maintenance),
            handler: maintenance => this.openCompleteMaintenancePopup(maintenance, vehicle),
          },
          {
            icon: 'bi bi-pencil-fill',
            title: this.$t('maintenance.title.edit'),
            handler: maintenance => this.openEditMaintenancePopup(vehicle, maintenance),
          },
          {
            icon: 'bi bi-copy',
            buttonClass: 'accent-action-button',
            title: this.$t('maintenance.title.copy'),
            handler: maintenance => this.copyMaintenance(vehicle, maintenance),
          },
          {
            icon: 'bi bi-trash',
            title: this.$t('generic.delete'),
            handler: maintenance => this.deleteMaintenance(maintenance),
          },
        ],
        actionsCellClass: 'rows align-items-center',
      };
    },
    isMaintenanceCompleted(maintenance) {
      return this.normalizeStatus(maintenance?.status) === MaintenanceStatus.Completed;
    },
    normalizeStatus(status) {
      if (status === null || status === undefined) {
        return null;
      }

      if (typeof status === 'object' && status.value !== undefined) {
        return Number(status.value);
      }

      const numberStatus = Number(status);
      if (!Number.isNaN(numberStatus)) {
        return numberStatus;
      }

      if (typeof status === 'string') {
        switch (status.toLowerCase()) {
          case 'upcoming':
            return MaintenanceStatus.Upcoming;
          case 'due':
            return MaintenanceStatus.Due;
          case 'overdue':
            return MaintenanceStatus.Overdue;
          case 'completed':
            return MaintenanceStatus.Completed;
        }
      }

      return null;
    },
    getStatusCount(maintenances, status) {
      return (maintenances ?? []).filter(x => this.normalizeStatus(x.status) === status).length;
    },
    sortMaintenancesByDefault(maintenances) {
      const statusPriority = {
        [MaintenanceStatus.Overdue]: 0,
        [MaintenanceStatus.Due]: 1,
        [MaintenanceStatus.Upcoming]: 2,
        [MaintenanceStatus.Completed]: 3,
      };

      return [...(maintenances ?? [])].sort((a, b) => {
        const firstPriority = statusPriority[this.normalizeStatus(a?.status)] ?? Number.MAX_SAFE_INTEGER;
        const secondPriority = statusPriority[this.normalizeStatus(b?.status)] ?? Number.MAX_SAFE_INTEGER;
        if (firstPriority !== secondPriority) {
          return firstPriority - secondPriority;
        }

        const firstName = (a?.name ?? '').toLowerCase();
        const secondName = (b?.name ?? '').toLowerCase();
        return firstName.localeCompare(secondName);
      });
    },
    getStatusLabel(status) {
      const normalizedStatus = this.normalizeStatus(status);
      const statusItem = MaintenanceStatus.get(normalizedStatus);
      const fallbackMap = {
        [MaintenanceStatus.Upcoming]: 'Upcoming',
        [MaintenanceStatus.Due]: 'Due',
        [MaintenanceStatus.Overdue]: 'Overdue',
        [MaintenanceStatus.Completed]: 'Completed',
      };

      if (!statusItem?.translationKey) {
        return fallbackMap[normalizedStatus] ?? '-';
      }

      const translated = this.$t(statusItem.translationKey);
      return translated === statusItem.translationKey ? (fallbackMap[normalizedStatus] ?? '-') : translated;
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined) {
        return '-';
      }

      const numericAmount = Number(amount);
      if (Number.isNaN(numericAmount)) {
        return '-';
      }

      return numericAmount.toFixed(2);
    },
    formatCompletionDate(value) {
      if (!value) {
        return '-';
      }

      const dateTimeMoment = dataManipulator.iso8601DateStringToLocalMoment(value);
      if (!dateTimeMoment?.isValid?.()) {
        return '-';
      }

      return dateTimeMoment.format(this.$t('settings.generic.date-time-format-moment'));
    },
    formatCompletionOdometer(value) {
      if (value === null || value === undefined) {
        return '-';
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return '-';
      }

      return (numericValue / 1000).toFixed(2);
    },
    formatTargetDate(value) {
      if (!value) {
        return null;
      }

      const dateMoment = dataManipulator.iso8601DateStringToLocalMoment(value);
      if (!dateMoment?.isValid?.()) {
        return null;
      }

      return dateMoment.format(this.$t('settings.generic.date-format-moment'));
    },
    formatOdometerValue(value) {
      if (value === null || value === undefined) {
        return null;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return numericValue.toFixed(2);
    },
    kilometersToMeters(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return Number((numericValue * 1000).toFixed(2));
    },
    metersToKilometers(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return numericValue / 1000;
    },
    getCurrentOdometerByDeviceId(deviceId) {
      const position = (this.positions ?? []).find(x => x?.deviceId === deviceId);
      if (!position) {
        return null;
      }

      const totalDistance = entityDataManager.getPositionAttribute(position, 'totalDistance', null);
      const numericTotalDistance = Number(totalDistance);
      if (totalDistance === null || Number.isNaN(numericTotalDistance)) {
        return null;
      }

      return numericTotalDistance / 1000;
    },
    formatPlannedStatusDetails(maintenance, deviceId) {
      const status = this.normalizeStatus(maintenance?.status);
      if (status !== MaintenanceStatus.Upcoming &&
          status !== MaintenanceStatus.Due &&
          status !== MaintenanceStatus.Overdue) {
        return '';
      }

      const parts = [];
      const kmLabel = this.$t('generic.kilometers');
      const targetOdometerInKm = this.metersToKilometers(maintenance?.targetOdometer ?? maintenance?.odometerPeriod);
      const targetOdometer = this.formatOdometerValue(targetOdometerInKm);
      if (targetOdometer !== null) {
        let remainingPart = null;
        const currentOdometer = this.getCurrentOdometerByDeviceId(deviceId);
        if (currentOdometer !== null) {
          const diff = Number(targetOdometer) - currentOdometer;
          const remainingKm = Math.abs(diff).toFixed(2);
          const relationKey = status === MaintenanceStatus.Overdue ? 'maintenance.status.before' : 'maintenance.status.after';
          remainingPart = `${this.$t(relationKey)} ${remainingKm} ${kmLabel}`;
        }
        if (remainingPart) {
          parts.push(remainingPart);
        }
        parts.push(`${this.$t('maintenance.status.at')} ${targetOdometer} ${kmLabel}`);
      }

      const targetDate = this.formatTargetDate(maintenance?.targetDate);
      if (targetDate) {
        parts.push(`${this.$t('maintenance.status.on')} ${targetDate}`);
      }

      if (parts.length === 0) {
        return '';
      }

      return ` - ${parts.join(', ')}`;
    },
  },
  data() {
    return {
      MaintenanceStatus,
      expandedVehicles: {},
      appliedGarageFilterDeviceIds: [],
    };
  },
};
</script>

<style scoped lang="scss">
.garage-page {
  .garage-filters {
    flex-wrap: wrap;
  }

  .search-button {
    margin-top: 1px;
  }

  .vehicle-title {
    gap: 12px;
  }

  .vehicle-name {
    font-weight: 700;
    font-size: 1rem;
  }

  .vehicle-name-row {
    gap: 8px;
  }

  .vehicle-plate {
    font-size: 0.9rem;
    color: #5f6b7a;
  }

  .status-counters {
    margin-left: auto;
    gap: 8px;
    flex-wrap: wrap;
  }

  .counter {
    font-size: 0.85rem;
    padding: 5px 10px;
    border-radius: 999px;
    font-weight: 600;
  }

  .counter.upcoming {
    background: #e9f5ff;
    color: #1f5f95;
  }

  .counter.due {
    background: #fff6db;
    color: #8d6400;
  }

  .counter.overdue {
    background: #ffe8eb;
    color: #9a2530;
  }

  .counter.completed {
    background: #e7f8ee;
    color: #1f7a48;
  }

  .maintenance-content {
    padding-bottom: 15px;
  }

  :deep(.maintenance-data-grid) {
    width: 100%;
    padding: 0;
    margin: 0;
  }

  :deep(.maintenance-data-grid td:last-child) {
    text-align: center;
    white-space: nowrap;
  }

  :deep(.maintenance-data-grid .table-action) {
    margin-right: 3px;
  }

  :deep(.maintenance-data-grid .table-action:last-child) {
    margin-right: 0;
  }

  :deep(.maintenance-data-grid .accent-action-button) {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    font-size: 1.4rem;
    padding-top: 1px;
  }

  .empty-maintenances {
    color: #6b7280;
    font-size: 0.95rem;
    padding: 10px 0;
  }
}
</style>
